import { useState, useRef, Fragment, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownFilter } from '../../../../components/Filters/DropdownFilter';
import { PlatformChooser } from './choosers/PlatformChooser';
import { AffiliationCountriesChooser } from './choosers/AffiliationCountriesChooser';
import { BotsChooser } from './choosers/BotsChooser';
import { IoCChooser } from './choosers/IoCChooser';
import { CountriesChooser } from './choosers/CountriesChooser';
import { AudienceChooser } from './choosers/AudienceChooser';
import { GeoGroupChooser } from './choosers/GeoGroupChooser';
import { BreakdownsFilter } from './BreakdownsFilter';
import { BreakdownsFilterProvider, useBreakdownsFilterContext } from './BreakdownsFilterContext';

import styles from './BreakdownsFilters.module.scss';

const defaultDropdownFilters = ['platforms', 'affiliations', 'bots', 'discreditations'];

export const BreakdownsFiltersContent = ({ onApply }) => {
  const { t } = useTranslation();
  const { state, applyDraftFilters } = useBreakdownsFilterContext();
  const [openDropdownFilters, setOpenDropdownFilters] = useState({});

  const handleDropdownOpenChange = (filterName, isOpen) => {
    setOpenDropdownFilters(prev => ({
      ...prev,
      [filterName]: isOpen,
    }));
  };

  const getMapDropdownFilters = () => ({
    platforms: (
      <DropdownFilter
        title={t('Platforms')}
        counter={state.draftFilters.platforms.value.length}
        isExclude={state.draftFilters.platforms.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('platforms', isOpen)}
      >
        <PlatformChooser />
      </DropdownFilter>
    ),
    affiliations: (
      <DropdownFilter
        title={t('State-affiliated')}
        counter={state.draftFilters.affiliations.value.length}
        isExclude={state.draftFilters.affiliations.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('affiliations', isOpen)}
      >
        <AffiliationCountriesChooser />
      </DropdownFilter>
    ),
    bots: (
      <DropdownFilter
        title={t('Inauthentic behavior')}
        counter={state.draftFilters.bots.value === null ? 0 : 1}
        isExclude={state.draftFilters.bots.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('bots', isOpen)}
      >
        <BotsChooser />
      </DropdownFilter>
    ),
    discreditations: (
      <DropdownFilter
        title={t('High risk accounts')}
        counter={state.draftFilters.discreditations.value.length}
        isExclude={state.draftFilters.discreditations.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('discreditations', isOpen)}
      >
        <IoCChooser />
      </DropdownFilter>
    ),
    countries: (
      <DropdownFilter
        title={t('Actor origin countries')}
        counter={state.draftFilters.countries.value.length}
        isExclude={state.draftFilters.countries.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('countries', isOpen)}
      >
        <CountriesChooser />
      </DropdownFilter>
    ),
    audience: (
      <DropdownFilter
        title={t('Audience')}
        counter={state.draftFilters.audience.value[0] > 0 || state.draftFilters.audience.value[1] < 1000000000 ? 1 : 0}
        isExclude={state.draftFilters.audience.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('audience', isOpen)}
      >
        <AudienceChooser />
      </DropdownFilter>
    ),
    geoGroup: (
      <DropdownFilter
        title={t('Actor origin country groups')}
        counter={state.draftFilters.geoGroup.value.length}
        isExclude={state.draftFilters.geoGroup.exclude}
        onOpenChange={(isOpen) => handleDropdownOpenChange('geoGroup', isOpen)}
      >
        <GeoGroupChooser />
      </DropdownFilter>
    ),
  });

  const filtersToShow = () => {
    const mapDropdownFilters = getMapDropdownFilters();
    const activeFilters = [];

    Object.entries(state.draftFilters).forEach(([filterName, filter]) => {
      if (mapDropdownFilters[filterName]) {
        const hasValue =
          filterName === 'bots'
            ? filter.value === true
            : filterName === 'audience'
            ? filter.value[0] > 0 || filter.value[1] < 1000000000
            : Array.isArray(filter.value) && filter.value.length > 0;

        const isOpen = openDropdownFilters[filterName];
        const shouldShow = hasValue || isOpen;

        if (shouldShow) {
          activeFilters.push(filterName);
        }
      }
    });

    const filtersToRender = [
      ...new Set([...defaultDropdownFilters, ...activeFilters]),
    ];

    return filtersToRender.map((filterName) => (
      <Fragment key={filterName}>{mapDropdownFilters[filterName]}</Fragment>
    ));
  };

  const [showApplyHint, setShowApplyHint] = useState(false);
  const isInitialMount = useRef(true);
  const hasInteracted = useRef(false);

  const handleApplyFilters = useCallback(() => {
    applyDraftFilters();
    setShowApplyHint(false);
    onApply?.();
  }, [applyDraftFilters, onApply]);

  const hasActiveFilters =
    state.draftFilters.platforms.value.length > 0 ||
    state.draftFilters.affiliations.value.length > 0 ||
    state.draftFilters.bots.value !== null ||
    state.draftFilters.discreditations.value.length > 0 ||
    state.draftFilters.countries.value.length > 0 ||
    state.draftFilters.audience.value[0] > 0 ||
    state.draftFilters.audience.value[1] < 1000000000 ||
    state.draftFilters.geoGroup.value.length > 0;

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    hasInteracted.current = true;
    setShowApplyHint(true);
  }, [
    state.draftFilters.platforms,
    state.draftFilters.affiliations,
    state.draftFilters.bots,
    state.draftFilters.discreditations,
    state.draftFilters.countries,
    state.draftFilters.audience,
    state.draftFilters.geoGroup,
  ]);

  const isButtonDisabled = !hasInteracted.current && !hasActiveFilters;

  return (
    <div className={styles.filtersWrapper}>
      <div className={styles.filtersContainer}>
        <BreakdownsFilter />
        {filtersToShow()}
      </div>
      <div className={styles.applyButtonWrapper}>
        {showApplyHint && <div className={styles.applyHint}>
          {t('Apply to update results')}
        </div>}
        <button
          className={styles.applyButton}
          onClick={handleApplyFilters}
          disabled={isButtonDisabled}
        >
          {t('Apply filters')}
        </button>
      </div>
    </div>
  );
};

export const BreakdownsFilters = () => {
  return (
    <BreakdownsFilterProvider>
      <BreakdownsFiltersContent />
    </BreakdownsFilterProvider>
  );
};
